import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import { AxiosError } from 'axios';
import {
  changeOrderDevice,
  changeStatusOrder,
  createOrderGroup,
  deleteOrder,
  deliverOrders,
  getDeviceStatistics,
  getDeviceStatisticsAll,
  getOrderDetail,
  getOrderNotes,
  getOrders,
  getOrderWorklist,
  matchOrder,
  ParamsChangeOrderDevice,
  ParamsChangeStatusOrder,
  ParamsCreateOrderGroup,
  ParamsDeleteOrder,
  ParamsDeliverOrders,
  ParamsMatchOrder,
  ParamsReceptOrder,
  ParamsUpdateOrder,
  ParamsUpdateOrderNote,
  ParamsUpdateOrderPriority,
  receptOrder,
  unmatchOrder,
  updateOrder,
  updateOrderNote,
  updateOrderPriority,
} from '../apis/order.api';
import { queryKey } from '../constants/queryClientKey';
import { DefaultErrorResponse } from '../interfaces';

const invalidationKeys = [
  queryKey.orders_interval,
  queryKey.orders,
  queryKey.study_detail,
  queryKey.orderWorklist_interval,
  queryKey.orderWorklist,
];

export function useGetDeviceStatistics(
  params: Parameters<typeof getDeviceStatistics>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.deviceStatistics, sortObjectKeys(params)],
    () => getDeviceStatistics(params),
    {
      ...options,
      onError: (err) => {
        console.error('getDeviceStatistics:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
}

export function useGetDeviceStatisticsAll(
  params: Parameters<typeof getDeviceStatisticsAll>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.deviceStatisticsAll, sortObjectKeys(params)],
    () => getDeviceStatisticsAll(params),
    {
      ...options,
      onError: (err) => {
        console.error('getDeviceStatisticsAll:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
}

export const useGetDeviceStatisticsInterval = (
  params: Parameters<typeof getDeviceStatistics>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.deviceStatistics_interval, sortObjectKeys(params)],
    () => getDeviceStatistics(params),
    {
      refetchInterval: 10_000,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
};

export function useGetOrders(
  params: Parameters<typeof getOrders>[0],
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof getOrders>>,
    AxiosError<DefaultErrorResponse>
  >
) {
  return useQuery(
    options?.queryKey || [queryKey.orders, sortObjectKeys(params)],
    () => getOrders(params),
    {
      onError: (err) => {
        console.error('useGetOrders:', err);
      },
      ...options,
    }
  );
}

export const useGetOrdersInterval = (
  params: Parameters<typeof getOrders>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.orders_interval, sortObjectKeys(params)],
    () => getOrders(params),
    {
      refetchInterval: 10_000,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrders>>>;
};

export function useGetOrderWorklist(
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.orderWorklist, sortObjectKeys(params)],
    () => getOrderWorklist(params),
    {
      ...options,
      onError: (err) => {
        console.error('useGetOrders:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;
}

export const useGetOrderWorklistInterval = (
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.orderWorklist_interval, sortObjectKeys(params)],
    () => getOrderWorklist(params),
    {
      getNextPageParam: defaultGetNextPageParam,
      // refetchInterval: 10_000,
      keepPreviousData: true,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;
};

export const useInfinityWorkList = (
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.orderWorklist, sortObjectKeys(params)],
    (e) =>
      getOrderWorklist({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;

  const data = queryInfo.data?.pages.map((page) => page.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
};

export function useGetOrderDetail(id, options?: UseQueryOptions) {
  return useQuery([queryKey.orderDetail, id], () => getOrderDetail(id), {
    ...options,
    onSuccess: (data: Awaited<ReturnType<typeof getOrderDetail>>) => {
      options?.onSuccess?.(data.data);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getOrderDetail>>>;
}

export function useMutationGetOrder(
  options?: Parameters<typeof useMutation>['2']
) {
  return useMutation<
    Awaited<ReturnType<typeof getOrderDetail>>,
    any,
    Parameters<typeof getOrderDetail>['0']
  >(async (params) => getOrderDetail(params), {
    onError: (err) => {
      console.error('useMutationGetOrderDetail:', err);
    },
    ...options,
  });
}

export function useCreateOrderGroup() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateOrderGroup) => createOrderGroup(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.orders_interval]);
        queryClient.invalidateQueries([queryKey.orders]);
      },
      onError: (err) => {
        console.error('useCreateOrderGroup:', err);
      },
    }
  );
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsUpdateOrder) => updateOrder(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.orders_interval]);
      queryClient.invalidateQueries([queryKey.orderWorklist_interval]);
      queryClient.invalidateQueries([queryKey.orderWorklist]);
      queryClient.invalidateQueries([queryKey.orderDetail]);
      queryClient.invalidateQueries([queryKey.orders]);
    },
    onError: (err) => {
      console.error('useUpdateOrder:', err);
    },
  });
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsDeleteOrder) => deleteOrder(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.orders_interval]);
    },
    onError: (err) => {
      console.error('useDeleteOrder:', err);
    },
  });
}

export function useMatchOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsMatchOrder) => matchOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useMatchOrder:', err);
    },
  });
}

export function useUnmatchOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsMatchOrder) => unmatchOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useUnmatchOrder:', err);
    },
  });
}

export function useReceptOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsReceptOrder) => receptOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useReceptOrder:', err);
    },
  });
}

export function useChangeOrderDevice() {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ParamsChangeOrderDevice) => changeOrderDevice(params),
    {
      onSuccess: async () => {
        for (const key of invalidationKeys) {
          await queryClient.invalidateQueries([key]);
        }
      },
      onError: (err) => {
        console.error('useReceptOrder:', err);
      },
    }
  );
}

export function useUpdateOrderPriority() {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ParamsUpdateOrderPriority) => updateOrderPriority(params),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries([queryKey.orders_interval]);
        queryClient.invalidateQueries([queryKey.orders]);
      },
      onError: (err) => {
        console.error('useUpdateOrderPriority:', err);
      },
    }
  );
}

export function useGetOrderNotes(id: string, options?: UseQueryOptions) {
  return useQuery([queryKey.orderNotes, id], () => getOrderNotes(id), {
    ...options,
    onError: (err) => {
      console.error('useGetOrderNotes:', err);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getOrderNotes>>>;
}

export function useUpdateOrderNote() {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ParamsUpdateOrderNote) => updateOrderNote(params),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries([queryKey.orderNotes]);
      },
      onError: (err) => {
        console.error('useUpdateOrderPriority:', err);
      },
    }
  );
}

export function useChangeStatusOrders() {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ParamsChangeStatusOrder) => changeStatusOrder(params),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries([queryKey.orders_interval]);
        queryClient.invalidateQueries([queryKey.orders]);
      },
      onError: (err) => {
        console.error('useChangeStatusOrders:', err);
      },
    }
  );
}

export function useDeliverOrders() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsDeliverOrders) => deliverOrders(params), {
    onSuccess: async () => {
      queryClient.invalidateQueries([queryKey.orders_interval]);
      queryClient.invalidateQueries([queryKey.orders]);
    },
    onError: (err) => {
      console.error('useDeliverOrders:', err);
    },
  });
}
